import React from 'react';

import { Box } from '@betterment/samba-react';
import { Helmet } from 'react-helmet';

const Root: React.FC<React.PropsWithChildren> = ({ children }) => (
  <Box
    css={{
      display: 'grid',
      gridTemplateAreas: {
        base: '"sidebar" "main"',
        xl: '"sidebar main"',
      },
      gridTemplateColumns: {
        base: '1fr',
        xl: 'auto 1fr',
      },
    }}
    data-js-target="main"
  >
    {children}
  </Box>
);

const Sidebar: React.FC<React.PropsWithChildren> = ({ children }) => (
  <Box
    css={{
      gridArea: 'sidebar',
      // FIXME: when creating proper z-index semantic tokens, this needs to be slightly higher than the other layout components due to async data loading some headers
      zIndex: 6,
    }}
  >
    {children}
  </Box>
);

type MainProps = {
  pageTitle: string | null;
};
const Main: React.FC<React.PropsWithChildren<MainProps>> = ({ children, pageTitle }) => (
  <Box
    css={{
      gridArea: 'main',
      bg: 'background',
      display: 'grid',
      gridTemplateColumns: '1fr minmax(min-content, 1200px) 1fr',
      gridTemplateRows: 'auto auto auto 1fr auto',
      gridTemplateAreas: `
          "notifications notifications notifications"
          "header header header"
          "stickyHeader stickyHeader stickyHeader"
          ". content ."
          ". footer ."
        `,
      gridColumnGap: {
        base: 'space.0',
        md: 'space.400',
      },
    }}
  >
    <Helmet titleTemplate="%s | Betterment" defaultTitle="Betterment">
      <title>{pageTitle}</title>
    </Helmet>
    {children}
  </Box>
);

const Banner: React.FC<React.PropsWithChildren> = ({ children }) => (
  <Box
    css={{
      gridArea: 'notifications',
      display: 'grid',
      gridTemplateColumns: 'auto minmax(min-content, 1200px) auto',
      gap: 'space.400',
      rowGap: {
        base: 'space.300',
        mdDown: 'space.100',
      },
      '&:has(~ #application-layout-header)': { bg: 'background.surface' },
      '&:has(~ #application-layout-sticky-header)': { bg: 'background.surface' },
    }}
  >
    {children}
  </Box>
);

const Header: React.FC<React.PropsWithChildren> = ({ children }) => (
  <Box
    id="application-layout-header"
    css={{
      bg: 'background.surface',
      gridArea: 'header',
      display: 'grid',
      gridTemplateColumns: 'subgrid',
      zIndex: 'compat-loader',
    }}
  >
    <Box
      css={{
        gridColumn: 2,
      }}
    >
      {children}
    </Box>
  </Box>
);

const StickyHeader: React.FC<React.PropsWithChildren> = ({ children }) => (
  <Box
    id="application-layout-sticky-header"
    css={{
      bg: 'background.surface',
      gridArea: 'stickyHeader',
      display: 'grid',
      gridTemplateColumns: 'subgrid',
      position: 'sticky',
      top: 0,
      zIndex: 'compat-loader',
    }}
  >
    <Box
      css={{
        gridColumn: 2,
      }}
    >
      {children}
    </Box>
  </Box>
);

const Content: React.FC<React.PropsWithChildren> = ({ children }) => (
  <Box
    css={{
      bg: 'background',
      gridArea: 'content',
      paddingTop: {
        base: 'space.200',
        md: 'space.400',
      },
    }}
  >
    {children}
  </Box>
);

const Footer: React.FC<React.PropsWithChildren> = ({ children }) => (
  <Box
    css={{
      gridArea: 'footer',
      paddingX: {
        base: 'space.400',
        smDown: 'space.300',
      },
      maxWidth: '1200px',
    }}
  >
    {children}
  </Box>
);

export const ApplicationLayout = Object.assign(Root, {
  Sidebar,
  Main,
  Banner,
  Header,
  StickyHeader,
  Content,
  Footer,
});
